import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getHousekeeperById, getHousekeeperExt, updateHousekeeper, updateResumeStatus, updateAbilitySalary, uploadCertificateImage, deleteCertificateImage, uploadPhoto, uploadWorkImage, uploadCookImage, deleteWorkImage, deleteCookImage, getHousekeeperAssessment, publishResume as publishResumeApi, getPublishList, uploadNannyCert, deleteNannyCert } from '../api/housekeeperApi';
import { uploadImageToCos } from '../api/cosApi';
import { parseJsonArray, parseLabelStr, parseExperiences, parseServiceCity, parseDateTokens, parseExperienceDate, formatExperienceDate, composeExperienceDate, validateExperiences } from '../utils/jsonParser';
import { ETHNICITY_OPTIONS, EDUCATION_OPTIONS, MARITAL_OPTIONS, ROLE_OPTIONS, ZODIAC_SIGNS, CHINESE_ZODIAC, CITY_OPTIONS } from '../constants/options';
import './HousekeeperDetailPage.css';

const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

export default function HousekeeperDetailPage({ id, userRole, navigateTo, initialShowShare }) {
  const [housekeeper, setHousekeeper] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const [labels, setLabels] = useState([]);
  const [experienceList, setExperienceList] = useState([]);
  const [certificateUrls, setCertificateUrls] = useState([]);
  const [workUrls, setWorkUrls] = useState([]);
  const [cookUrls, setCookUrls] = useState([]);
  const [nannyCertUrl, setNannyCertUrl] = useState('');
  const [nannyCertName, setNannyCertName] = useState('育婴师资格证');
  const [historicalSalary, setHistoricalSalary] = useState('');
  const [expectedSalary, setExpectedSalary] = useState('');
  const [abilitySalary, setAbilitySalary] = useState('');
  const [selfIntroduction, setSelfIntroduction] = useState('');
  const [serviceCities, setServiceCities] = useState([]);
  const [showAllWork, setShowAllWork] = useState(false);
  const [showAllCook, setShowAllCook] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showShare, setShowShare] = useState(initialShowShare || false);

  // Edit modals
  const [showHeaderEdit, setShowHeaderEdit] = useState(false);
  const [headerEditData, setHeaderEditData] = useState({});
  const [showLabelEdit, setShowLabelEdit] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [showCityEdit, setShowCityEdit] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [expireDays, setExpireDays] = useState(14);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCreated, setShareCreated] = useState(false);

  // Unsaved changes tracking
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [dirtySections, setDirtySections] = useState({ salary: false, selfIntro: false, experience: false });
  const originalDataRef = useRef({ salary: {}, selfIntro: '', experience: [] });
  const pendingActionRef = useRef(null);

  useEffect(() => { if (id) loadDetail(id); }, [id]);

  // Check for dirty state and show confirmation（工作经历已在离开前自动保存，此处只检查薪资与个人简介）
  const checkUnsavedChanges = (callback) => {
    const dirty = {
      salary: false,
      selfIntro: false,
      experience: false
    };

    // Check salary
    if (historicalSalary !== originalDataRef.current.salary.historicalSalary ||
        expectedSalary !== originalDataRef.current.salary.expectedSalary ||
        abilitySalary !== originalDataRef.current.salary.abilitySalary) {
      dirty.salary = true;
    }

    // Check self introduction
    if (selfIntroduction !== originalDataRef.current.selfIntro) {
      dirty.selfIntro = true;
    }

    const hasDirty = dirty.salary || dirty.selfIntro;

    if (hasDirty) {
      setDirtySections(dirty);
      pendingActionRef.current = callback;
      setShowUnsavedModal(true);
    } else {
      callback();
    }
  };

  const handleSaveAndLeave = async () => {
    setShowUnsavedModal(false);

    // Save dirty sections（工作经历已自动保存）
    if (dirtySections.salary) {
      await saveSalary();
    }
    if (dirtySections.selfIntro) {
      await saveSelfIntro();
    }

    // Execute pending action
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  };

  const handleLeaveWithoutSave = () => {
    setShowUnsavedModal(false);
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await getHousekeeperById(id);
      const h = res.data;
      const status = h.resume_status || 'draft';
      setHousekeeper(h);
      setIsPublished(status === 'published');
      setLabels(parseLabelStr(h.label));
      setExperienceList(decorateExperiences(parseExperiences(h.housekeeping_experience)));
      setCertificateUrls(parseJsonArray(h.certificate_url));
      setWorkUrls(parseJsonArray(h.work_url));
      setCookUrls(parseJsonArray(h.cook_url));
      setNannyCertUrl(h.nanny_cert_url || '');
      setNannyCertName(h.role === '月嫂' ? '母婴证' : '育婴师资格证');
      setHistoricalSalary(h.historical_salary || '');
      setExpectedSalary(h.expected_salary ? String(h.expected_salary) : '');
      setAbilitySalary(h.ability_salary ? String(h.ability_salary) : '');
      setSelfIntroduction(h.self_introduction || '');

      // Load assessment data
      try {
        const assessmentRes = await getHousekeeperAssessment(id);
        setAssessment(assessmentRes.data);
      } catch (e) { console.error('Failed to load assessment', e); }

      // Load ext data to get ai_request_count
      try {
        const extRes = await getHousekeeperExt(id);
        if (extRes.data) {
          setHousekeeper(prev => ({ ...prev, ai_request_count: extRes.data.ai_request_count || 0 }));
        }
      } catch (e) { console.error('Failed to load ext', e); }

      setServiceCities(parseServiceCity(h.service_city));

      // Save original data for dirty checking
      originalDataRef.current = {
        salary: {
          historicalSalary: h.historical_salary || '',
          expectedSalary: h.expected_salary ? String(h.expected_salary) : '',
          abilitySalary: h.ability_salary ? String(h.ability_salary) : ''
        },
        selfIntro: h.self_introduction || '',
        experience: decorateExperiences(parseExperiences(h.housekeeping_experience))
      };
    } catch (e) { alert('加载失败'); }
  };

  const goBack = async () => {
    // 离开前强制保存工作经历
    if (!(await autoSaveExperience())) return;
    checkUnsavedChanges(() => navigateTo('home'));
  };

  const goToBackgroundCheck = async () => {
    try {
      // 先检查是否有背调记录
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/housekeepers/${id}/background-check/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.code === 200 && res.data.data) {
        // 有记录，跳转到结果页
        navigateTo('background-check-result', { id, checkId: res.data.data.check_id });
      } else {
        // 无记录，跳转到查询页
        navigateTo('background-check', { id });
      }
    } catch (error) {
      console.error('检查背调记录失败', error);
      // 失败时默认跳转到查询页
      navigateTo('background-check', { id });
    }
  };

  const handleCreateShare = async () => {
    try {
      const res = await publishResumeApi(id, expireDays);
      setShareUrl(res.data.share_url);
      setShareCreated(true);
    } catch (e) {
      alert('创建分享链接失败：' + (e.response?.data?.detail || e.message));
    }
  };

  // 获取该阿姨最新背调的总体信用状态：返回 'normal'/'abnormal'/'history'，无记录或失败返回 null
  const getOverallStatus = async (hid) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/housekeepers/${hid}/background-check/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.code === 200 && res.data.data) {
        return res.data.data.overall_status || null;
      }
      return null;
    } catch (e) {
      console.error('获取背调状态失败', e);
      return null;
    }
  };

  const publishResume = async () => {
    // 发布前强制保存工作经历
    if (!(await autoSaveExperience())) return;
    checkUnsavedChanges(async () => {
      // 发布前校验背调信用状态
      const overallStatus = await getOverallStatus(id);
      // 无背调记录：不允许发布，提示先完成背调
      if (!overallStatus) {
        alert('该阿姨尚未进行背调查询，请先完成背调后再发布');
        return;
      }
      // 信用异常：不允许发布，状态保持待发布
      if (overallStatus === 'abnormal') {
        alert(`${housekeeper.name || '阿姨'}有信用问题，不允许发布`);
        return;
      }
      // normal / history：允许发布
      if (!window.confirm('确定要发布这份简历吗？')) return;
      try {
        await updateResumeStatus(id, 'published');
        alert('发布成功');
        loadDetail(id);
      } catch (e) { alert('发布失败'); }
    });
  };

  const reEditResume = async () => {
    try {
      await updateResumeStatus(id, 'pending');
      alert('已恢复编辑状态');
      loadDetail(id);
    } catch (e) { alert('操作失败'); }
  };

  // Header edit - open with all fields
  const openHeaderEdit = () => {
    const h = housekeeper;
    setHeaderEditData({
      name: h.name || '',
      gender: h.gender || '',
      age: h.age ? String(h.age) : '',
      birth_date: h.birth_date || '',
      ethnicity: h.ethnicity || '',
      education: h.education || '',
      zodiac_sign: h.zodiac_sign || '',
      chinese_zodiac: h.chinese_zodiac || '',
      role: h.role || '',
      birthplace: h.birthplace || '',
      marital_status: h.marital_status || '',
    });
    setShowHeaderEdit(true);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const accessUrl = await uploadImageToCos(file, 'head-image', userInfo.user_id);
      await uploadPhoto(id, accessUrl);
      loadDetail(id);
      alert('头像上传成功');
    } catch (err) { alert('上传失败'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const saveHeaderEdit = async () => {
    try {
      const data = { ...headerEditData };
      if (data.age) data.age = parseInt(data.age);
      else delete data.age;
      await updateHousekeeper(id, data);
      setShowHeaderEdit(false);
      loadDetail(id);
      alert('保存成功');
    } catch (e) { alert('保存失败'); }
  };

  // Label edit
  const addLabel = () => { if (newLabel.trim()) { setLabels([...labels, newLabel.trim()]); setNewLabel(''); } };
  const removeLabel = (idx) => { setLabels(labels.filter((_, i) => i !== idx)); };
  const saveLabelEdit = async () => {
    try { await updateHousekeeper(id, { label: JSON.stringify(labels) }); setShowLabelEdit(false); loadDetail(id); alert('保存成功'); }
    catch (e) { alert('保存失败'); }
  };

  // Salary save (inline, no modal)
  const saveSalary = async () => {
    try {
      const updateData = {};
      updateData.expected_salary = expectedSalary ? parseInt(expectedSalary) : null;
      updateData.historical_salary = historicalSalary || null;
      await updateHousekeeper(id, updateData);
      if (abilitySalary) await updateAbilitySalary(id, parseInt(abilitySalary));
      else await updateHousekeeper(id, { ability_salary: null });
      alert('薪资保存成功');
      // Update original data to reflect saved state
      originalDataRef.current.salary = { historicalSalary, expectedSalary, abilitySalary };
      loadDetail(id);
    } catch (e) { alert('保存失败'); }
  };

  // Service city save
  const saveCity = async () => {
    try { await updateHousekeeper(id, { service_city: JSON.stringify(serviceCities) }); setShowCityEdit(false); loadDetail(id); alert('保存成功'); }
    catch (e) { alert('保存失败'); }
  };

  // Self introduction
  const saveSelfIntro = async () => {
    try {
      await updateHousekeeper(id, { self_introduction: selfIntroduction });
      alert('保存成功');
      // Update original data to reflect saved state
      originalDataRef.current.selfIntro = selfIntroduction;
    }
    catch (e) { alert('保存失败'); }
  };

  // Experience
  // 由 {date,desc} 派生出四个年月输入框的回填字段，避免编辑时反复解析 date 字符串
  const pad2 = (n) => (n < 10 ? '0' + n : '' + n);
  const decorateExperience = (exp) => {
    const fields = parseExperienceDate((exp && exp.date) || '');
    return {
      ...exp,
      dateStartYear: fields.startYear,
      dateStartMonth: fields.startMonth ? pad2(fields.startMonth) : '',
      dateEndYear: fields.endYear,
      dateEndMonth: fields.endMonth ? pad2(fields.endMonth) : ''
    };
  };
  const decorateExperiences = (list) => (list || []).map(decorateExperience);

  const addExperience = () => setExperienceList([...experienceList, decorateExperience({ date: '', desc: '' })]);
  const removeExperience = (idx) => setExperienceList(experienceList.filter((_, i) => i !== idx));

  // 编辑时间段某个字段（startYear/startMonth/endYear/endMonth）时，直接缓存原始输入值
  const updateDateField = (idx, field, value) => {
    const key = 'date' + field.charAt(0).toUpperCase() + field.slice(1);
    const list = [...experienceList];
    list[idx] = { ...list[idx], [key]: value };
    setExperienceList(list);
  };

  const sortExperiencesByDate = (list) => {
    const parseStartDate = (dateStr) => {
      const tokens = parseDateTokens(dateStr);
      if (!tokens || tokens.length === 0) return { year: 9999, month: 12 };
      return { year: tokens[0].year, month: tokens[0].month || 1 };
    };
    return [...list].sort((a, b) => {
      const da = parseStartDate(a.date);
      const db = parseStartDate(b.date);
      if (da.year !== db.year) return da.year - db.year;
      return da.month - db.month;
    });
  };

  // 校验并保存工作经历；silent 为 true 时不弹成功/失败提示。返回是否保存成功。
  const doSaveExperience = async (silent) => {
    // 先由四个输入字段拼回 date 字符串，再进行校验与保存
    const withDate = experienceList.map((exp) => ({
      ...exp,
      date: composeExperienceDate({
        startYear: exp.dateStartYear,
        startMonth: exp.dateStartMonth,
        endYear: exp.dateEndYear,
        endMonth: exp.dateEndMonth
      })
    }));

    // 保存前校验：起止年月完整 + 时间段不重叠
    const error = validateExperiences(withDate);
    if (error) { alert(error); return false; }

    try {
      // 规范化日期为标准展示格式，并按开始时间排序
      const normalized = withDate.map((exp) => ({ ...exp, date: formatExperienceDate(exp.date) }));
      const sorted = sortExperiencesByDate(normalized);
      const decorated = decorateExperiences(sorted);
      setExperienceList(decorated);
      // 只保存 {date, desc}，避免把回填字段写入后端
      await updateHousekeeper(id, { housekeeping_experience: JSON.stringify(decorated.map((e) => ({ date: e.date, desc: e.desc }))) });
      // Update original data to reflect saved state
      originalDataRef.current.experience = JSON.parse(JSON.stringify(decorated));
      if (!silent) alert('保存成功');
      return true;
    }
    catch (e) { if (!silent) alert('保存失败'); return false; }
  };

  const saveExperience = () => doSaveExperience(false);

  // 离开页面时自动保存工作经历（有改动才保存），返回是否保存成功（无改动视为成功）
  const autoSaveExperience = async () => {
    const origExp = JSON.stringify(originalDataRef.current.experience);
    const currExp = JSON.stringify(experienceList);
    if (origExp === currExp) return true;
    return doSaveExperience(true);
  };

  // Certificate add/delete
  const addCertificate = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      for (const file of files) {
        const accessUrl = await uploadImageToCos(file, 'certificate-image', userInfo.user_id);
        await uploadCertificateImage(id, accessUrl);
      }
      loadDetail(id);
      alert('上传成功');
    } catch (err) { alert('上传失败'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const delCertificate = async (url) => {
    if (!window.confirm('确定删除证书图片？')) return;
    try { await deleteCertificateImage(id, url); loadDetail(id); alert('删除成功'); }
    catch (e) { alert('删除失败'); }
  };

  // Nanny certificate add/delete
  const addNannyCert = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const accessUrl = await uploadImageToCos(file, 'nanny-cert', userInfo.user_id);
      await uploadNannyCert(id, accessUrl);
      loadDetail(id);
      alert('上传成功');
    } catch (err) { alert('上传失败'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const delNannyCert = async () => {
    if (!window.confirm('确定删除该证书图片？')) return;
    try {
      await deleteNannyCert(id, nannyCertUrl);
      setNannyCertUrl('');
      alert('删除成功');
    } catch (e) { alert('删除失败'); }
  };

  // Work photo add/delete
  const addWorkPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      for (const file of files) {
        const accessUrl = await uploadImageToCos(file, 'work-image', userInfo.user_id);
        await uploadWorkImage(id, accessUrl);
      }
      loadDetail(id);
      alert('上传成功');
    } catch (err) { alert('上传失败'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const delWorkPhoto = async (url) => {
    if (!window.confirm('确定删除工作展示图片？')) return;
    try { await deleteWorkImage(id, url); loadDetail(id); alert('删除成功'); }
    catch (e) { alert('删除失败'); }
  };

  // Cook photo add/delete
  const addCookPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      for (const file of files) {
        const accessUrl = await uploadImageToCos(file, 'cook-image', userInfo.user_id);
        await uploadCookImage(id, accessUrl);
      }
      loadDetail(id);
      alert('上传成功');
    } catch (err) { alert('上传失败'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const delCookPhoto = async (url) => {
    if (!window.confirm('确定删除厨艺展示图片？')) return;
    try { await deleteCookImage(id, url); loadDetail(id); alert('删除成功'); }
    catch (e) { alert('删除失败'); }
  };

  const handleCopyShareLink = () => {
    const link = `${window.location.origin}/detail?id=${id}`;
    navigator.clipboard.writeText(link).then(() => alert('链接已复制'));
  };

  const visibleWork = showAllWork ? workUrls : workUrls.slice(0, 9);
  const visibleCook = showAllCook ? cookUrls : cookUrls.slice(0, 9);

  return (
    <div className="detail-container">
      {/* Header with back button */}
      <div className="detail-back-header">
        <button className="detail-back-btn" onClick={goBack}>← 返回</button>
      </div>
      {/* Header */}
      <div className="detail-header">
        <div className="avatar-section">
          {housekeeper.photo_url ? <img className="avatar" src={housekeeper.photo_url} alt="" /> : <div className="avatar-placeholder">👤</div>}
          {!isPublished && (
            <label className="avatar-edit-btn">
              <span className="avatar-edit-icon">📷</span>
              <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
            </label>
          )}
        </div>
        <div className="name-section">
          <span className="name">{housekeeper.name || '未填写姓名'}</span>
          {housekeeper.out_number && <div className="out-number">NO: {housekeeper.out_number}</div>}
          {housekeeper.role && <span className="role-tag">{housekeeper.role}</span>}
          <div className="header-info-row">
            {housekeeper.birthplace && <span>{housekeeper.birthplace}</span>}
            {housekeeper.age && <span> · {housekeeper.age}岁</span>}
            {housekeeper.education && <span> · {housekeeper.education}</span>}
          </div>
          <div className="header-info-row">
            {housekeeper.phone && <span>{housekeeper.phone}</span>}
            {housekeeper.gender && <span> · {housekeeper.gender}</span>}
            {housekeeper.ethnicity && <span> · {housekeeper.ethnicity}</span>}
            {housekeeper.zodiac_sign && <span> · {housekeeper.zodiac_sign}</span>}
            {housekeeper.chinese_zodiac && <span> · {housekeeper.chinese_zodiac}</span>}
            {housekeeper.marital_status && <span> · {housekeeper.marital_status}</span>}
          </div>
          <div className="idcard-original-row">
            <button className="idcard-original-btn" onClick={() => navigateTo('idcard-image', { id })}>身份证原图</button>
          </div>
        </div>
        {!isPublished && <button className="header-edit-fab" onClick={openHeaderEdit}><span className="fab-icon">✏️</span><span className="fab-text">编辑</span></button>}
      </div>

      {/* Tags - always show with edit button */}
      <div className="tags-section">
        <div className="tags-left">
          {labels.length > 0
            ? labels.map((l, i) => <span key={i} className="tag">{l}</span>)
            : <span className="empty-field">暂无标签</span>
          }
        </div>
        {!isPublished && <button className="edit-btn-small" onClick={() => setShowLabelEdit(true)}><span className="edit-icon">✎</span></button>}
      </div>

      {/* Salary - always show, inline textboxes for pending mode */}
      <div className="salary-section">
        <div className="salary-left" style={{ flex: 1 }}>
          <div className="salary-item">
            <span className="salary-label">历史薪资</span>
            {!isPublished ? (
              <input className="salary-inline-input" value={historicalSalary} onChange={(e) => setHistoricalSalary(e.target.value)} placeholder="AI提取或手动填写" />
            ) : (
              <span className="salary-value">{historicalSalary || '暂无'}</span>
            )}
          </div>
          <div className="salary-item">
            <span className="salary-label">期望薪资</span>
            {!isPublished ? (
              <input className="salary-inline-input" type="number" value={expectedSalary} onChange={(e) => setExpectedSalary(e.target.value)} placeholder="元/月" />
            ) : (
              <span className="salary-value">{expectedSalary ? `${expectedSalary}元/月` : '暂无'}</span>
            )}
          </div>
          <div className="salary-item">
            <span className="salary-label">能力薪资</span>
            {!isPublished ? (
              <input className="salary-inline-input" type="number" value={abilitySalary} onChange={(e) => setAbilitySalary(e.target.value)} placeholder="元/月" />
            ) : (
              <span className="salary-value">{abilitySalary ? `${abilitySalary}元/月` : '暂无'}</span>
            )}
          </div>
        </div>
        <div className="salary-actions">
          <button className="save-btn-inline" onClick={() => navigateTo('salary-flow', { id })}>薪资流水</button>
          {!isPublished && <button className="save-btn-inline" onClick={saveSalary}>保存</button>}
        </div>
      </div>

      {/* Service city - display only */}
      <div className="info-card city-card">
        <div className="card-title-row">
          <span className="card-title">接单城市</span>
        </div>
        <span className="city-value">{serviceCities.length > 0 ? serviceCities.join(' ') : '暂无'}</span>
      </div>

      {/* Assessment display */}
      <div className="info-card assessment-card">
        <div className="detail-row">
          <span className="detail-label">内部测评</span>
          <span className="detail-value">
            {assessment && (assessment.total_score > 0 || assessment.id)
              ? <>{assessment.total_score || 0}分（{assessment.assessment_level || '未评级'}）</>
              : <span className="empty-hint">暂无测评</span>}
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-label"></span>
          {housekeeper.ai_request_count >= 1 ? (
            <a className="review-link" onClick={() => navigateTo('assessment-review', { id })}>
              测评复核 →
            </a>
          ) : (
            <span className="empty-hint">AI处理中，请稍候...</span>
          )}
        </div>
      </div>

      {/* Nanny Certificate */}
      {(nannyCertUrl || !isPublished) && (
        <div className="info-card photo-card">
          <div className="card-title">{nannyCertName}</div>
          <div className="photo-grid">
            {nannyCertUrl && (
              <div className="photo-item">
                <img className="cert-img" src={nannyCertUrl} alt={nannyCertName} />
                {!isPublished && <button className="photo-delete-badge" onClick={delNannyCert}>✕</button>}
              </div>
            )}
            {!isPublished && !nannyCertUrl && (
              <label className="photo-add-item">
                <span className="photo-add-plus">+</span>
                <input type="file" accept="image/*" onChange={addNannyCert} style={{ display: 'none' }} />
              </label>
            )}
          </div>
        </div>
      )}

      {/* Certificates - always show */}
      <div className="info-card photo-card">
        <div className="card-title">技能证书</div>
        <div className="photo-grid">
          {certificateUrls.map((url, i) => (
            <div key={i} className="photo-item">
              <img className="cert-img" src={url} alt={`证书${i + 1}`} />
              {!isPublished && <button className="photo-delete-badge" onClick={() => delCertificate(url)}>✕</button>}
            </div>
          ))}
          {!isPublished && (
            <label className="photo-add-item">
              <span className="photo-add-plus">+</span>
              <input type="file" accept="image/*" multiple onChange={addCertificate} style={{ display: 'none' }} />
            </label>
          )}
        </div>
      </div>

      {/* Self introduction - always show */}
      <div className="info-card intro-card">
        <div className="card-title">个人简介</div>
        {!isPublished ? (
          <>
            <textarea className="intro-edit-area" value={selfIntroduction} onChange={(e) => setSelfIntroduction(e.target.value)} placeholder="请输入个人简介" />
            <button className="save-btn-inline" onClick={saveSelfIntro}>保存</button>
          </>
        ) : (
          <div className="intro-content">{selfIntroduction || '暂无个人简介'}</div>
        )}
      </div>

      {/* Work experience - always show */}
      <div className="info-card">
        <div className="card-title">工作经历</div>
        <div className="experience-list">
          {experienceList.map((exp, i) => {
            return (
              <div key={i} className="experience-item">
                {!isPublished ? (
                  <>
                    <div className="exp-date-edit-row">
                      <input className="exp-y-minput exp-y-minput--year" type="number" value={exp.dateStartYear} onChange={(e) => updateDateField(i, 'startYear', e.target.value)} />
                      <span className="exp-date-fix">年</span>
                      <select className="exp-month-select" value={exp.dateStartMonth} onChange={(e) => updateDateField(i, 'startMonth', e.target.value)}>
                        <option value=""></option>
                        {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <span className="exp-date-fix">月 至</span>
                      <input className="exp-y-minput exp-y-minput--year" type="number" value={exp.dateEndYear} onChange={(e) => updateDateField(i, 'endYear', e.target.value)} />
                      <span className="exp-date-fix">年</span>
                      <select className="exp-month-select" value={exp.dateEndMonth} onChange={(e) => updateDateField(i, 'endMonth', e.target.value)}>
                        <option value=""></option>
                        {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <span className="exp-date-fix">月</span>
                    </div>
                    <textarea className="exp-desc-edit" value={exp.desc} onChange={(e) => { const list = [...experienceList]; list[i].desc = e.target.value; setExperienceList(list); }} placeholder="工作内容" />
                    <button className="remove-exp" onClick={() => removeExperience(i)}>删除</button>
                  </>
                ) : (
                  <>
                    <div className="exp-date">{formatExperienceDate(exp.date)}</div>
                    <div className="exp-desc">{exp.desc}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {experienceList.length === 0 && isPublished && <div className="empty-section">暂无工作经历</div>}
        {!isPublished && <button className="add-exp-btn" onClick={addExperience}>添加工作经历</button>}
        {!isPublished && <button className="save-btn-inline" onClick={saveExperience}>保存</button>}
      </div>

      {/* Work photos - always show title, with + add button */}
      <div className="info-card photo-card">
        <div className="card-title">工作展示</div>
        <div className="photo-grid">
          {visibleWork.map((url, i) => (
            <div key={i} className="photo-item">
              <img className="cert-img" src={url} alt="" />
              {!isPublished && <button className="photo-delete-badge" onClick={() => delWorkPhoto(url)}>✕</button>}
            </div>
          ))}
          {!isPublished && (
            <label className="photo-add-item">
              <span className="photo-add-plus">+</span>
              <input type="file" accept="image/*" multiple onChange={addWorkPhoto} style={{ display: 'none' }} />
            </label>
          )}
        </div>
        {workUrls.length === 0 && isPublished && <div className="empty-section">暂无工作展示图片</div>}
        {workUrls.length > 9 && <div className="show-more-btn" onClick={() => setShowAllWork(!showAllWork)}>{showAllWork ? '收起' : '展示更多'}</div>}
      </div>

      {/* Cook photos - always show title, with + add button */}
      <div className="info-card photo-card">
        <div className="card-title">厨艺展示</div>
        <div className="photo-grid">
          {visibleCook.map((url, i) => (
            <div key={i} className="photo-item">
              <img className="cert-img" src={url} alt="" />
              {!isPublished && <button className="photo-delete-badge" onClick={() => delCookPhoto(url)}>✕</button>}
            </div>
          ))}
          {!isPublished && (
            <label className="photo-add-item">
              <span className="photo-add-plus">+</span>
              <input type="file" accept="image/*" multiple onChange={addCookPhoto} style={{ display: 'none' }} />
            </label>
          )}
        </div>
        {cookUrls.length === 0 && isPublished && <div className="empty-section">暂无厨艺展示图片</div>}
        {cookUrls.length > 9 && <div className="show-more-btn" onClick={() => setShowAllCook(!showAllCook)}>{showAllCook ? '收起' : '展示更多'}</div>}
      </div>

      {/* Bottom actions */}
      <div className="action-bar">
        <button className="action-btn" onClick={goBack}>返回</button>
        <button className="action-btn credit-btn" onClick={goToBackgroundCheck}>信用查询</button>
        {!isPublished && <button className="action-btn primary" onClick={publishResume}>发布简历</button>}
        {isPublished && <button className="action-btn re-edit" onClick={reEditResume}>重新编辑简历</button>}
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedModal && (
        <div className="modal-overlay" onClick={() => setShowUnsavedModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">未保存的更改</div>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>您编辑了{dirtySections.salary && '薪资'}{dirtySections.salary && (dirtySections.selfIntro || dirtySections.experience) ? '、' : ''}{dirtySections.selfIntro && '个人简介'}{dirtySections.selfIntro && dirtySections.experience ? '和' : ''}{dirtySections.experience && '工作经历'}，是否要保存？</p>
            </div>
            <div className="modal-btns">
              <button className="btn btn-secondary" onClick={handleLeaveWithoutSave}>否</button>
              <button className="btn btn-primary" onClick={handleSaveAndLeave}>是</button>
            </div>
          </div>
        </div>
      )}

      {/* Header Edit Modal - Redesigned with scrollable sections */}
      {showHeaderEdit && (
        <div className="modal-overlay" onClick={() => setShowHeaderEdit(false)}>
          <div className="modal-content modal-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-sticky">
              <div className="modal-title">编辑基本信息</div>
              <button className="modal-close-btn" onClick={() => setShowHeaderEdit(false)}>✕</button>
            </div>
            <div className="modal-body-scroll">
              {/* Section: Identity */}
              <div className="modal-section">
                <div className="modal-section-title">身份信息</div>
                <div className="form-group">
                  <label className="form-label">姓名</label>
                  <input className="form-input" value={headerEditData.name} onChange={(e) => setHeaderEditData({...headerEditData, name: e.target.value})} placeholder="请输入姓名" />
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="form-label">性别</label>
                    <select className="form-input" value={headerEditData.gender} onChange={(e) => setHeaderEditData({...headerEditData, gender: e.target.value})}>
                      <option value="">请选择</option>
                      <option value="女">女</option>
                      <option value="男">男</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">年龄</label>
                    <input className="form-input" type="number" value={headerEditData.age} onChange={(e) => setHeaderEditData({...headerEditData, age: e.target.value})} placeholder="岁" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">出生日期</label>
                  <input className="form-input" value={headerEditData.birth_date} onChange={(e) => setHeaderEditData({...headerEditData, birth_date: e.target.value})} placeholder="YYYY-MM-DD" />
                </div>
                <div className="form-group">
                  <label className="form-label">籍贯</label>
                  <input className="form-input" value={headerEditData.birthplace} onChange={(e) => setHeaderEditData({...headerEditData, birthplace: e.target.value})} placeholder="请输入籍贯" />
                </div>
              </div>

              {/* Section: Background */}
              <div className="modal-section">
                <div className="modal-section-title">个人背景</div>
                <div className="form-group">
                  <label className="form-label">民族</label>
                  <select className="form-input" value={headerEditData.ethnicity} onChange={(e) => setHeaderEditData({...headerEditData, ethnicity: e.target.value})}>
                    <option value="">请选择</option>
                    {ETHNICITY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="form-label">学历</label>
                    <select className="form-input" value={headerEditData.education} onChange={(e) => setHeaderEditData({...headerEditData, education: e.target.value})}>
                      <option value="">请选择</option>
                      {EDUCATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">婚姻状况</label>
                    <select className="form-input" value={headerEditData.marital_status} onChange={(e) => setHeaderEditData({...headerEditData, marital_status: e.target.value})}>
                      <option value="">请选择</option>
                      {MARITAL_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section: Work & Attributes */}
              <div className="modal-section">
                <div className="modal-section-title">工作属性</div>
                <div className="form-group">
                  <label className="form-label">角色</label>
                  <select className="form-input" value={headerEditData.role} onChange={(e) => setHeaderEditData({...headerEditData, role: e.target.value})}>
                    <option value="">请选择</option>
                    {ROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="form-label">生肖</label>
                    <select className="form-input" value={headerEditData.chinese_zodiac} onChange={(e) => setHeaderEditData({...headerEditData, chinese_zodiac: e.target.value})}>
                      <option value="">请选择</option>
                      {CHINESE_ZODIAC.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">星座</label>
                    <select className="form-input" value={headerEditData.zodiac_sign} onChange={(e) => setHeaderEditData({...headerEditData, zodiac_sign: e.target.value})}>
                      <option value="">请选择</option>
                      {ZODIAC_SIGNS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer-sticky">
              <button className="btn btn-secondary" onClick={() => setShowHeaderEdit(false)}>取消</button>
              <button className="btn btn-primary" onClick={saveHeaderEdit}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* Label Edit Modal */}
      {showLabelEdit && (
        <div className="modal-overlay" onClick={() => setShowLabelEdit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">编辑标签</div>
            <div className="label-list">
              {labels.map((l, i) => <div key={i} className="label-item"><span>{l}</span><span className="remove-tag" onClick={() => removeLabel(i)}>✕</span></div>)}
            </div>
            <div className="add-label-row">
              <input className="form-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="输入新标签" />
              <button className="btn btn-primary" style={{ padding: '10px 16px' }} onClick={addLabel}>添加</button>
            </div>
            <div className="modal-btns">
              <button className="btn btn-secondary" onClick={() => setShowLabelEdit(false)}>取消</button>
              <button className="btn btn-primary" onClick={saveLabelEdit}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* City Edit Modal */}
      {showCityEdit && (
        <div className="modal-overlay" onClick={() => setShowCityEdit(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">编辑接单城市</div>
            <div className="city-multi-select-modal">
              {CITY_OPTIONS.map(city => (
                <label key={city} className="city-option">
                  <input
                    type="checkbox"
                    checked={serviceCities.includes(city)}
                    onChange={(e) => {
                      const cities = e.target.checked
                        ? [...serviceCities, city]
                        : serviceCities.filter(c => c !== city);
                      setServiceCities(cities);
                    }}
                  />
                  <span>{city}</span>
                </label>
              ))}
            </div>
            <div className="modal-btns">
              <button className="btn btn-secondary" onClick={() => setShowCityEdit(false)}>取消</button>
              <button className="btn btn-primary" onClick={saveCity}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {showShare && (
        <div className="modal-overlay" onClick={() => setShowShare(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">分享简历</div>
            {housekeeper.name && <div className="share-name">{housekeeper.name} 的简历</div>}

            {/* Expire days selection */}
            {!shareCreated && (
              <div className="expire-section">
                <div className="expire-label">分享有效期</div>
                <div className="expire-options">
                  <button className={`expire-btn ${expireDays === 7 ? 'active' : ''}`} onClick={() => setExpireDays(7)}>一周</button>
                  <button className={`expire-btn ${expireDays === 14 ? 'active' : ''}`} onClick={() => setExpireDays(14)}>两周</button>
                  <button className={`expire-btn ${expireDays === 30 ? 'active' : ''}`} onClick={() => setExpireDays(30)}>一个月</button>
                </div>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '16px' }}
                  onClick={handleCreateShare}>确认发布分享</button>
              </div>
            )}

            {/* Share actions after creation */}
            {shareCreated && (
              <>
                <div className="share-options">
                  <div className="share-option" onClick={() => alert('请在微信中打开本链接进行分享')}>
                    <div className="share-icon-box wechat"><span className="share-icon-text">💬</span></div>
                    <span className="share-option-label">分享到微信</span>
                  </div>
                  <div className="share-option" onClick={() => {
                    navigator.clipboard.writeText(shareUrl).then(() => alert('链接已复制'));
                  }}>
                    <div className="share-icon-box link"><span className="share-icon-text">🔗</span></div>
                    <span className="share-option-label">复制链接</span>
                  </div>
                </div>
                <div className="share-link-display">{shareUrl}</div>
                <div className="expire-info">有效期：{expireDays === 7 ? '一周' : expireDays === 14 ? '两周' : '一个月'}</div>
              </>
            )}

            <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setShowShare(false); setShareCreated(false); }}>关闭</button>
          </div>
        </div>
      )}

      {uploading && (
        <div className="uploading-overlay">
          <div className="uploading-content">
            <div className="spinner"></div>
            <div className="uploading-text">正在上传...</div>
          </div>
        </div>
      )}
    </div>
  );
}
